import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Coffee, Star, Sparkles, CreditCard, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const SupportUsPage = () => {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [loading, setLoading] = useState(false);

  const donationAmounts = [
    { value: 5, label: '5€', icon: Coffee, description: 'Un café pour l\'équipe' },
    { value: 10, label: '10€', icon: Heart, description: 'Soutien symbolique' },
    { value: 20, label: '20€', icon: Star, description: 'Contribution généreuse' },
    { value: 50, label: '50€', icon: Sparkles, description: 'Super soutien' },
  ];

  const handleDonate = async () => {
    const amount = selectedAmount === 'custom' ? parseFloat(customAmount) : selectedAmount;
    
    if (!amount || amount < 1) {
      toast.error('Veuillez sélectionner ou entrer un montant valide');
      return;
    }

    setLoading(true);
    try {
      // Use current origin in production, env variable in development
      const baseUrl = window.location.origin.includes('xperience-guides.com') 
        ? window.location.origin 
        : process.env.REACT_APP_BACKEND_URL;
      const API_URL = baseUrl + '/api';
      const response = await fetch(`${API_URL}/donations/public-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ amount: amount * 100 }) // Stripe uses cents
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Erreur lors de la création du paiement');
      }
    } catch (error) {
      toast.error('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen pt-20 pb-12" data-testid="support-us-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmOTczMTYiIGZpbGwtb3BhY2l0eT0iMC4wNCI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iNCIvPjwvZz48L2c+PC9zdmc+')] opacity-50" />
        
        <div className="max-w-4xl mx-auto px-4 py-16 text-center relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', duration: 0.6 }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-gradient-to-br from-rose-400 to-orange-500 flex items-center justify-center shadow-lg"
          >
            <Heart className="w-12 h-12 text-white" fill="white" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-rose-600 to-orange-600 bg-clip-text text-transparent"
          >
            Soutenez XPerience
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Aidez-nous à connecter voyageurs et passionnés locaux partout dans le monde
          </motion.p>
        </div>
      </div>

      {/* Why Support Section */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-3xl shadow-xl p-8 md:p-12 -mt-12 relative z-10"
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            Pourquoi nous soutenir ?
          </h2>
          
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: '🌍', title: 'Tourisme responsable', desc: 'Nous promouvons un tourisme authentique et respectueux des communautés locales' },
              { icon: '🤝', title: 'Économie locale', desc: 'Chaque expérience soutient directement les guides et artisans de la région' },
              { icon: '💚', title: 'Projet indépendant', desc: 'XPerience est un projet passionné, sans investisseurs externes' },
            ].map((item, i) => (
              <div key={i} className="text-center p-4">
                <span className="text-4xl block mb-3">{item.icon}</span>
                <h3 className="font-bold text-gray-800 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Donation Amount Selection */}
          <div className="border-t pt-8">
            <h3 className="text-xl font-bold text-gray-800 mb-6 text-center">
              Choisissez votre contribution
            </h3>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {donationAmounts.map((amount) => (
                <button
                  key={amount.value}
                  onClick={() => { setSelectedAmount(amount.value); setCustomAmount(''); }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    selectedAmount === amount.value
                      ? 'border-rose-500 bg-rose-50 shadow-md'
                      : 'border-gray-200 hover:border-rose-300 bg-white'
                  }`}
                >
                  <amount.icon className={`w-8 h-8 mx-auto mb-2 ${
                    selectedAmount === amount.value ? 'text-rose-500' : 'text-gray-400'
                  }`} />
                  <span className="font-bold text-xl block">{amount.label}</span>
                  <span className="text-xs text-gray-500">{amount.description}</span>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="flex flex-col md:flex-row gap-4 items-center justify-center mb-8">
              <span className="text-gray-600">Ou montant libre :</span>
              <div className="relative">
                <Input
                  type="number"
                  min="1"
                  placeholder="Montant"
                  value={customAmount}
                  onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount('custom'); }}
                  className="w-32 pr-8 text-center"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
              </div>
            </div>

            {/* Donate Button */}
            <div className="text-center">
              <Button
                onClick={handleDonate}
                disabled={loading || (!selectedAmount && !customAmount)}
                size="lg"
                className="bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-white px-12 py-6 text-lg font-semibold shadow-lg"
                data-testid="donate-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                {loading ? 'Redirection...' : 'Faire un don'}
              </Button>
              
              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
                <Check className="w-4 h-4 text-green-500" />
                Paiement sécurisé via Stripe
              </div>
            </div>
          </div>
        </motion.div>

        {/* Thank You Message */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="text-center mt-12 p-8 bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl"
        >
          <span className="text-4xl block mb-4">🙏</span>
          <h3 className="text-xl font-bold text-gray-800 mb-2">Merci pour votre soutien !</h3>
          <p className="text-gray-600">
            Chaque contribution nous aide à développer la plateforme et à connecter
            toujours plus de voyageurs avec des expériences authentiques.
          </p>
        </motion.div>
      </div>
    </main>
  );
};

export default SupportUsPage;
